import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, GraduationCap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { trackFunnelEvent } from '../lib/funnel-tracker';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
    const navigate = useNavigate();
    const { reloadUser } = useAuth();
    const [error, setError] = useState(null);

    useEffect(() => {
        // session_id viene en el fragmento (#session_id=...)
        const hash = window.location.hash || '';
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const sessionId = params.get('session_id');

        if (!sessionId) {
            setError('No se recibió session_id de Google. Por favor intenta nuevamente.');
            return;
        }

        (async () => {
            try {
                const res = await fetch(`${API}/api/auth/google/session`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sessionId }),
                });
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    const err = j.detail || 'No se pudo verificar la sesión Google';
                    trackFunnelEvent('abandoned', { last_action: 'google_auth_error', error: err });
                    throw new Error(err);
                }
                const data = await res.json().catch(() => ({}));
                // limpiar fragmento
                window.history.replaceState(null, '', '/auth/callback');
                // Cargar usuario en el contexto (cookie ya está seteada)
                if (reloadUser) {
                    try { await reloadUser(); } catch (_) {}
                }
                trackFunnelEvent('register_completed', {
                    user_id: data?.user?.id, meta: { provider: 'google' },
                });
                trackFunnelEvent('first_session', { user_id: data?.user?.id });
                toast.success('¡Bienvenido/a a Portafolio Digital Docente!');
                // Si es usuario nuevo (sin onboarding completado) → wizard rápido,
                // si no → dashboard normal.
                const onboardingDone = data?.user?.perfil?.onboarding_completado;
                navigate(onboardingDone ? '/empezar' : '/onboarding', { replace: true });
            } catch (e) {
                setError(e.message || 'Error al iniciar sesión con Google');
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
            <div className="w-full max-w-md text-center" data-testid="auth-callback-view">
                <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900">
                    <GraduationCap className="h-6 w-6 text-white" />
                </div>
                {error ? (
                    <>
                        <AlertCircle className="mx-auto h-8 w-8 text-rose-600" />
                        <h1 className="mt-3 font-heading text-lg font-bold text-slate-900">No pudimos completar el ingreso</h1>
                        <p className="mt-2 text-sm text-slate-600">{error}</p>
                        <button
                            onClick={() => navigate('/login')}
                            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                            data-testid="auth-callback-retry"
                        >
                            Volver a iniciar sesión
                        </button>
                    </>
                ) : (
                    <>
                        <Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />
                        <h1 className="mt-3 font-heading text-lg font-bold text-slate-900">Validando tu sesión…</h1>
                        <p className="mt-2 text-sm text-slate-500">Estamos preparando tu Portafolio Digital Docente.</p>
                    </>
                )}
            </div>
        </div>
    );
}
